/**
 * Hero Slider JavaScript
 * Handles Swiper initialization and custom functionality
 */

class HeroSlider {
  constructor(element) {
    this.element = element;
    this.slider = element.querySelector('.hero-slider');
    this.sectionId = element.dataset.sectionId;
    this.swiper = null;
    this.settings = this.getSettings();
    
    this.init();
  }

  /**
   * Get slider settings from data attributes
   */
  getSettings() {
    const slider = this.slider;
    return {
      autoplay: slider.dataset.autoplay === 'true',
      speed: parseInt(slider.dataset.speed) || 1000,
      effect: slider.dataset.effect || 'slide',
      arrows: slider.dataset.arrows === 'true',
      dots: slider.dataset.dots === 'true'
    };
  }

  /**
   * Initialize the slider
   */
  init() {
    // Initialize immediately without waiting for images
    // This prevents the blank/white screen issue
    setTimeout(() => {
      this.initSwiper();
      this.bindEvents();
      this.element.classList.add('loaded');
    }, 100);
  }

  /**
   * Wait for all images in slides to load
   */
  waitForImages() {
    const images = this.slider.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      return new Promise((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.addEventListener('load', resolve);
          img.addEventListener('error', resolve); // Resolve even on error
        }
      });
    });

    return Promise.all(imagePromises);
  }

  /**
   * Initialize Swiper instance
   */
  initSwiper() {
    // Find navigation elements
    const nextEl = this.element.querySelector('.hero-arrow-next');
    const prevEl = this.element.querySelector('.hero-arrow-prev');
    const paginationEl = this.element.querySelector('.hero-pagination');
    
    console.log('Hero Slider Debug:', {
      nextEl: nextEl,
      prevEl: prevEl,
      paginationEl: paginationEl,
      settings: this.settings
    });

    const swiperConfig = {
      // Basic settings
      effect: this.settings.effect,
      speed: this.settings.speed,
      loop: true,
      grabCursor: true,
      watchSlidesProgress: true,
      
      // Autoplay
      autoplay: this.settings.autoplay ? {
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,

      // Navigation arrows (section-specific)
      navigation: this.settings.arrows && nextEl && prevEl ? {
        nextEl: nextEl,
        prevEl: prevEl,
      } : false,

      // Pagination dots (section-specific)
      pagination: this.settings.dots && paginationEl ? {
        el: paginationEl,
        clickable: true,
        dynamicBullets: false,
        renderBullet: (index, className) => {
          return `<span class="${className}" aria-label="Go to slide ${index + 1}"></span>`;
        }
      } : false,

      // Keyboard control
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // Accessibility
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}'
      },

      // Breakpoints for responsive behavior
      breakpoints: {
        320: {
          spaceBetween: 0,
        },
        768: {
          spaceBetween: 0,
        },
        1024: {
          spaceBetween: 0,
        }
      },

      // Event callbacks
      on: {
        init: (swiper) => {
          this.swiper = swiper;
          this.onSliderInit();
        },
        slideChange: (swiper) => {
          this.swiper = swiper;
          this.onSlideChange();
        },
        autoplayStart: () => {
          this.onAutoplayStart();
        },
        autoplayStop: () => {
          this.onAutoplayStop();
        }
      }
    };

    // Initialize Swiper
    try {
      this.swiper = new Swiper(this.slider, swiperConfig);
      console.log('Hero Slider initialized successfully:', this.swiper);
    } catch (error) {
      console.error('Hero Slider initialization failed:', error);
    }
  }

  /**
   * Bind additional events
   */
  bindEvents() {
    // Pause autoplay on hover
    if (this.settings.autoplay) {
      this.slider.addEventListener('mouseenter', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      });

      this.slider.addEventListener('mouseleave', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.start();
        }
      });
    }

    // Handle visibility change (pause when tab is not active)
    document.addEventListener('visibilitychange', () => {
      if (this.swiper && this.swiper.autoplay) {
        if (document.hidden) {
          this.swiper.autoplay.stop();
        } else {
          if (this.settings.autoplay) {
            this.swiper.autoplay.start();
          }
        }
      }
    });

    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
        }
      }, 250);
    });

    // Handle touch events for better mobile experience
    this.handleTouchEvents();
  }

  /**
   * Handle touch events for mobile
   */
  handleTouchEvents() {
    let startY = 0;
    let startX = 0;

    this.slider.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.slider.addEventListener('touchmove', (e) => {
      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const diffY = Math.abs(currentY - startY);
      const diffX = Math.abs(currentX - startX);

      // If horizontal swipe is more significant, prevent vertical scroll
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Callback when slider initializes
   */
  onSliderInit() {
    // Add custom classes or perform initialization tasks
    this.slider.classList.add('hero-slider-initialized');
    
    // Trigger custom event
    this.element.dispatchEvent(new CustomEvent('heroSlider:init', {
      detail: { swiper: this.swiper, slider: this }
    }));
  }

  /**
   * Callback when slide changes
   */
  onSlideChange() {
    // Add slide change animations or effects
    const activeSlide = this.slider.querySelector('.swiper-slide-active');
    if (activeSlide) {
      // Trigger animations for active slide content
      this.animateSlideContent(activeSlide);
    }

    // Trigger custom event
    this.element.dispatchEvent(new CustomEvent('heroSlider:slideChange', {
      detail: { 
        swiper: this.swiper, 
        slider: this,
        activeIndex: this.swiper ? this.swiper.activeIndex : 0
      }
    }));
  }

  /**
   * Animate slide content when it becomes active
   */
  animateSlideContent(slide) {
    const title = slide.querySelector('.hero-title');
    const subtitle = slide.querySelector('.hero-subtitle');
    const button = slide.querySelector('.hero-cta-button');
    const image = slide.querySelector('.hero-foreground-image');

    // Reset animations
    [title, subtitle, button, image].forEach(el => {
      if (el) {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
      }
    });

    // Animate elements in sequence
    const animateElement = (element, delay = 0) => {
      if (!element) return;
      
      setTimeout(() => {
        element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        element.style.opacity = '1';
        element.style.transform = 'translateY(0)';
      }, delay);
    };

    animateElement(title, 100);
    animateElement(subtitle, 200);
    animateElement(button, 300);
    animateElement(image, 150);
  }

  /**
   * Callback when autoplay starts
   */
  onAutoplayStart() {
    this.slider.classList.add('autoplay-active');
  }

  /**
   * Callback when autoplay stops
   */
  onAutoplayStop() {
    this.slider.classList.remove('autoplay-active');
  }

  /**
   * Public method to go to specific slide
   */
  goToSlide(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }

  /**
   * Public method to start autoplay
   */
  startAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.start();
    }
  }

  /**
   * Public method to stop autoplay
   */
  stopAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
  }

  /**
   * Public method to destroy slider
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Public method to update slider
   */
  update() {
    if (this.swiper) {
      this.swiper.update();
    }
  }
}

/**
 * Initialize all hero sliders on the page
 */
function initHeroSliders() {
  const sliders = document.querySelectorAll('.hero-slider-wrapper');
  const sliderInstances = [];

  sliders.forEach(slider => {
    const instance = new HeroSlider(slider);
    sliderInstances.push(instance);
    
    // Store instance on element for external access
    slider.heroSlider = instance;
  });

  return sliderInstances;
}

/**
 * Handle Shopify section events (theme editor)
 */
function handleShopifyEvents() {
  // Handle section load in theme editor
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId && event.target.querySelector('.hero-slider-wrapper')) {
      const slider = event.target.querySelector('.hero-slider-wrapper');
      if (slider && !slider.heroSlider) {
        new HeroSlider(slider);
      }
    }
  });

  // Handle section unload in theme editor
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.detail.sectionId) {
      const slider = event.target.querySelector('.hero-slider-wrapper');
      if (slider && slider.heroSlider) {
        slider.heroSlider.destroy();
        slider.heroSlider = null;
      }
    }
  });

  // Handle block select in theme editor
  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('.hero-slider-wrapper');
    if (slider && slider.heroSlider) {
      const blockIndex = Array.from(event.target.parentNode.children).indexOf(event.target);
      slider.heroSlider.goToSlide(blockIndex);
      slider.heroSlider.stopAutoplay();
    }
  });

  // Handle block deselect in theme editor
  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target.closest('.hero-slider-wrapper');
    if (slider && slider.heroSlider && slider.heroSlider.settings.autoplay) {
      slider.heroSlider.startAutoplay();
    }
  });
}

/**
 * Initialize when DOM is ready
 */
function initializeHeroSliders() {
  // Check if Swiper is available
  if (typeof Swiper === 'undefined') {
    console.warn('Swiper library not loaded, retrying in 500ms...');
    setTimeout(initializeHeroSliders, 500);
    return;
  }
  
  initHeroSliders();
  handleShopifyEvents();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHeroSliders);
} else {
  initializeHeroSliders();
}

// Fallback initialization after a delay (only if no sliders were initialized)
setTimeout(() => {
  if (typeof Swiper !== 'undefined') {
    const uninitializedSliders = document.querySelectorAll('.hero-slider-wrapper:not(.loaded)');
    if (uninitializedSliders.length > 0) {
      console.log('Initializing fallback hero sliders...', uninitializedSliders.length);
      uninitializedSliders.forEach(slider => {
        if (!slider.heroSlider) {
          new HeroSlider(slider);
        }
      });
    }
  }
}, 1000);

/**
 * Export for external use
 */
window.HeroSlider = HeroSlider;
window.initHeroSliders = initHeroSliders;
